import { useCallback, useEffect, useState } from 'react'

const NONE = []

// Which neighbouring items (`selector`) inside an element that wraps them sit
// side by side: beside[i] is true when item i and item i + 1 have the same top
// offset (one row), false when i + 1 has wrapped below. Measured, not guessed
// from a width: the element is watched with a ResizeObserver, so the answer
// follows it as it resizes and its items wrap or unwrap. Without a
// ResizeObserver the answer is empty (no pair is side by side, so an arrow
// between two items stays hidden rather than risk pointing at nothing).
//
// Returns [ref, beside]: put ref on the element. What the answer shows must
// not change the layout it measured (the account-linking strip draws its
// arrows in the gap after a step, out of the flow), or showing it could wrap
// the items and hide it again.
export const useSideBySide = selector => {
  const [element, setElement] = useState(null)
  const [beside, setBeside] = useState(NONE)

  const measure = useCallback(() => {
    if (!element) return
    const tops = Array.from(element.querySelectorAll(selector)).map(item => item.offsetTop)
    const next = tops.slice(1).map((top, index) => top === tops[index])
    // the same answer keeps the same array: nothing to draw again
    setBeside(prior => (prior.length === next.length && prior.every((value, index) => value === next[index]) ? prior : next))
  }, [element, selector])

  useEffect(() => {
    if (!element || typeof window.ResizeObserver !== 'function') {
      setBeside(NONE)
      return undefined
    }
    const observer = new window.ResizeObserver(measure)
    observer.observe(element)
    measure()
    return () => observer.disconnect()
  }, [element, measure])

  return [setElement, beside]
}
